import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, LogOut, Loader2, Paperclip, PenSquare, X, Mail, Trash2, Users, Inbox,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";

// Espace d'échange communautaire : accès par simple email de cliente (sans mot de passe).
const storeKey = "l30m_board";

const loadSession = () => {
  try { return JSON.parse(sessionStorage.getItem(storeKey) || "null"); } catch { return null; }
};

export default function Board() {
  const [session, setSession] = useState(loadSession());
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(null); // annonce ouverte (lecture)
  const [composing, setComposing] = useState(false);

  const refresh = (s = session) => {
    if (!s) return;
    api.get("/board/posts", { params: { email: s.email } })
      .then((r) => {
        setPosts(r.data.items || []);
        const isAdmin = !!r.data.admin;
        if (isAdmin !== !!s.admin) { // rattrape les sessions enregistrées avant l'arrivée de la modération
          const updated = isAdmin ? { ...s, admin: true, name: "Le 30 du mois", code: "ADMIN" } : { ...s, admin: false };
          sessionStorage.setItem(storeKey, JSON.stringify(updated));
          setSession(updated);
        }
      })
      .catch((e) => {
        if (e.response?.status === 403) { sessionStorage.removeItem(storeKey); setSession(null); }
        else toast.error("Chargement impossible.");
      });
  };

  useEffect(() => { refresh(); }, [session?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return <Gate onEnter={(s) => { sessionStorage.setItem(storeKey, JSON.stringify(s)); setSession(s); }} />;

  const quit = () => { sessionStorage.removeItem(storeKey); setSession(null); setOpen(null); };

  return (
    <div className="min-h-screen bg-[#0A0B10] text-white" data-testid="board-page">
      <header className="border-b border-white/10 px-6 md:px-10 py-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Link to="/" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors" data-testid="board-home-link">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="font-serif text-xl md:text-2xl">L'espace d'échange</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A0B2CE]">
              {session.name} · <span className="text-gold">{session.code}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {session.admin && (
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold border border-gold/40 rounded-full px-4 py-2" data-testid="board-admin-badge">
              Modération
            </span>
          )}
          <button onClick={() => setComposing(true)} data-testid="board-new-btn"
            className="inline-flex items-center gap-2 bg-gold text-[#0A0B10] px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] hover:bg-[#B5952F] transition-colors">
            <PenSquare className="w-3.5 h-3.5" /> Nouvelle annonce
          </button>
          <button onClick={quit} data-testid="board-quit-btn"
            className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors" title="Quitter l'espace">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <p className="text-sm text-[#A0B2CE] mb-8 max-w-2xl">
          Annonces de la communauté : recherche d'emploi, structure cherchant un·e collaborateur·rice, entraide…
          Les annonces restent visibles 6 semaines. Pour répondre, écrivez directement à l'auteure ou l'auteur.
        </p>

        <div className="mb-10 border border-gold/25 rounded-2xl bg-gold/[0.05] px-6 py-5" data-testid="board-charter">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold mb-3">Bon usage de l'espace</p>
          <ul className="space-y-2 text-sm text-[#C7CAD1] leading-relaxed">
            <li><strong className="text-white">Entre compagnies</strong> — l'espace est réservé aux clients du 30 du mois ; chaque annonce est signée et votre email est visible des personnes qui répondent.</li>
            <li><strong className="text-white">Six semaines</strong> — les annonces s'effacent ensuite automatiquement ; republiez la vôtre si elle est toujours d'actualité.</li>
            <li><strong className="text-white">En direct</strong> — les échanges se font directement entre compagnies ; Le 30 du mois n'intervient pas dans les transactions.</li>
            <li><strong className="text-white">L'équipe veille</strong> — une annonce peut être retirée par la modération, et vous pouvez supprimer la vôtre à tout moment.</li>
          </ul>
        </div>

        {posts === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 border border-white/10 rounded-2xl bg-white/[0.02]" data-testid="board-empty">
            <Inbox className="w-10 h-10 text-[#5A6B8C] mx-auto mb-4" />
            <p className="font-serif text-xl mb-2">Aucune annonce pour le moment</p>
            <p className="text-sm text-[#A0B2CE]">Soyez la première à publier avec « Nouvelle annonce ».</p>
          </div>
        ) : (
          <div className="border border-white/10 rounded-2xl overflow-hidden divide-y divide-white/10" data-testid="board-list">
            {posts.map((p) => (
              <button key={p.id} onClick={() => setOpen(p)} data-testid={`board-post-${p.id}`}
                className="w-full text-left px-5 md:px-7 py-5 flex items-center gap-4 hover:bg-white/[0.04] transition-colors">
                <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-gold" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-medium truncate">{p.author_name}</span>
                    {p.team && (
                      <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[#0A0B10] bg-gold rounded-full px-2 py-0.5 shrink-0" data-testid={`board-team-${p.id}`}>
                        Le 30 du mois
                      </span>
                    )}
                    <span className="font-mono text-[10px] text-[#5A6B8C] shrink-0">
                      {new Date(p.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                  <p className="font-serif text-lg text-gold truncate">{p.title}</p>
                  <p className="text-sm text-[#A0B2CE] truncate">{p.body}</p>
                </div>
                {p.has_attachment && <Paperclip className="w-4 h-4 text-[#A0B2CE] shrink-0" data-testid={`board-clip-${p.id}`} />}
              </button>
            ))}
          </div>
        )}
      </main>

      {open && <Reader post={open} session={session} onClose={() => setOpen(null)}
        onDeleted={() => { setOpen(null); refresh(); toast.success("Annonce supprimée."); }} />}
      {composing && <Composer session={session} onClose={() => setComposing(false)}
        onPosted={() => { setComposing(false); refresh(); toast.success("Annonce publiée — visible 6 semaines."); }} />}
    </div>
  );
}

function Gate({ onEnter }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/board/enter", { email });
      onEnter({ email: email.toLowerCase().trim(), name: r.data.name, code: r.data.code, admin: !!r.data.admin });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Entrée impossible pour le moment.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0B10] text-white flex items-center justify-center p-6" data-testid="board-gate">
      <div className="w-full max-w-md border border-white/10 rounded-2xl p-10 bg-white/[0.03]">
        <Users className="w-10 h-10 text-gold mb-6" />
        <h1 className="font-serif text-3xl mb-3">L'espace d'échange</h1>
        <p className="text-sm text-[#A0B2CE] mb-8">
          Réservé aux compagnies clients du 30 du mois. Saisissez l'adresse email rattachée à votre compte
          — aucun mot de passe n'est nécessaire.
        </p>
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Votre email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="board-email"
              className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300" />
          </div>
          {error && <p className="text-sm text-[#E74C3C]" data-testid="board-gate-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="board-enter-btn"
            className="w-full inline-flex items-center justify-center gap-3 bg-gold text-[#0A0B10] py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrer dans l'espace"}
          </button>
        </form>
        <Link to="/" className="block mt-8 font-mono text-[11px] uppercase tracking-[0.12em] text-[#A0B2CE] hover:text-gold transition-colors" data-testid="board-back-home">
          ← Retour au site
        </Link>
      </div>
    </div>
  );
}

function Reader({ post, session, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/board/posts/${post.id}`, { params: { email: session.email } });
      onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Suppression impossible.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="board-reader">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#0A0B10] border border-white/15 rounded-2xl p-8 md:p-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5A6B8C] mb-1 flex items-center gap-2 flex-wrap">
              {post.author_name}
              {post.team && (
                <span className="text-[9px] text-[#0A0B10] bg-gold rounded-full px-2 py-0.5" data-testid="board-team-reader">Le 30 du mois</span>
              )}
              · {new Date(post.created_at).toLocaleDateString("fr-FR")}
            </p>
            <h2 className="font-serif text-2xl md:text-3xl text-gold">{post.title}</h2>
          </div>
          <button onClick={onClose} data-testid="board-reader-close"
            className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[#D9E2F1] leading-relaxed whitespace-pre-wrap mb-8">{post.body}</p>
        <div className="flex items-center gap-3 flex-wrap">
          <a href={`mailto:${post.author_email}?subject=${encodeURIComponent(`Re : ${post.title} — espace d'échange du 30 du mois`)}`}
            data-testid="board-reply-btn"
            className="inline-flex items-center gap-2 bg-gold text-[#0A0B10] px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] hover:bg-[#B5952F] transition-colors">
            <Mail className="w-3.5 h-3.5" /> Répondre à {post.author_name}
          </a>
          {post.has_attachment && (
            <a href={`${API}/board/posts/${post.id}/attachment?email=${encodeURIComponent(session.email)}`} target="_blank" rel="noopener noreferrer"
              data-testid="board-attachment-btn"
              className="inline-flex items-center gap-2 border border-white/25 px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] hover:bg-white/10 transition-colors">
              <Paperclip className="w-3.5 h-3.5" /> {post.attachment_name || "Pièce jointe"}
            </a>
          )}
          {(post.can_delete || post.own) && (
            <button onClick={del} disabled={busy} data-testid="board-delete-btn"
              className="inline-flex items-center gap-2 border border-[#C0392B]/60 text-[#E74C3C] px-5 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.12em] hover:bg-[#C0392B] hover:text-white transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Supprimer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Composer({ session, onClose, onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { email: session.email, title, body };
      if (file) {
        if (file.size > 5 * 1024 * 1024) throw new Error("Pièce jointe trop lourde — 5 Mo maximum.");
        payload.attachment_name = file.name;
        payload.attachment_type = file.type;
        payload.attachment_b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result).split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      await api.post("/board/posts", payload);
      onPosted();
    } catch (err) {
      setError(err.response ? (formatApiErrorDetail(err.response.data?.detail) || "Publication impossible.")
                            : (err.message || "Publication impossible."));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="board-composer">
      <form onSubmit={submit} className="w-full max-w-2xl bg-[#0A0B10] border border-white/15 rounded-2xl p-8 md:p-10 space-y-7"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl md:text-3xl">Nouvelle annonce</h2>
          <button type="button" onClick={onClose} data-testid="board-composer-close"
            className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Titre *</label>
          <input type="text" required maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex. Recherche régisseur·rice pour tournée printemps" data-testid="board-form-title"
            className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-lg transition-colors duration-300" />
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Message *</label>
          <textarea required rows={5} maxLength={5000} value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="Décrivez votre recherche ou votre proposition… (vos coordonnées : votre email de compte est affiché aux lectrices et lecteurs)"
            data-testid="board-form-body"
            className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-base resize-none transition-colors duration-300" />
        </div>
        <div>
          <label className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#A0B2CE]">Pièce jointe (PDF ou image, 5 Mo max)</label>
          <input type="file" accept=".pdf,image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="board-form-file"
            className="mt-2 block w-full text-sm text-[#A0B2CE] file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:font-mono file:text-[11px] file:uppercase file:tracking-[0.12em] file:bg-white/10 file:text-white hover:file:bg-white/20 file:transition-colors" />
        </div>
        {error && <p className="text-sm text-[#E74C3C]" data-testid="board-form-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="board-form-submit"
          className="w-full inline-flex items-center justify-center gap-3 bg-gold text-[#0A0B10] py-4 rounded-full font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#B5952F] transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publier l'annonce"}
        </button>
      </form>
    </div>
  );
}
